// src/components/Login/VerifyEmail.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Mail, 
  CheckCircle, 
  AlertCircle, 
  RefreshCw,
  ArrowLeft,
  Send,
  Shield,
  Clock,
  Sparkles
} from 'lucide-react';

/**
 * Componente de verificação de e-mail ultra premium
 * Nível: Google/Airbnb/Spotify
 * 
 * Características:
 * - Validação de token (mock)
 * - 4 estados diferentes (verificando, sucesso, erro, expirado)
 * - Timer para reenvio
 * - Animações fluidas
 * - Feedback visual premium
 * - Modo offline-first
 * - Preparado para integração real
 */

// ============================================
// ⏱️ CONSTANTES
// ============================================
const RESEND_COOLDOWN = 60; // segundos
const MOCK_TOKEN = 'verify-123456-demo-token-789';
const MOCK_EMAIL = 'usuario@email.com'; // Email mock para demonstração

const VerifyEmail = ({ onSuccess, onBackToLogin }) => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  // ============================================
  // 📊 ESTADOS
  // ============================================
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error' | 'expired'
  const [email, setEmail] = useState(MOCK_EMAIL);
  const [cooldown, setCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // ============================================
  // 🔍 VALIDAÇÃO DE TOKEN (MOCK)
  // ============================================
  useEffect(() => {
    const verifyToken = async () => {
      setStatus('verifying');
      
      try {
        // 🔥 MOCK: Simular verificação de token
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        const tokenToVerify = token || MOCK_TOKEN;
        
        // Simular 80% de chance de sucesso (para demonstração)
        const random = Math.random();
        
        if (random < 0.8) {
          // Sucesso
          setStatus('success');
          
          // Disparar evento
          window.dispatchEvent(new CustomEvent('auth:email:verified', {
            detail: { 
              email: MOCK_EMAIL,
              token: tokenToVerify,
              timestamp: new Date().toISOString()
            }
          }));
          
          // Callback de sucesso
          if (onSuccess) {
            onSuccess(MOCK_EMAIL);
          }
        } else if (random < 0.9) {
          // Expirado
          setStatus('expired');
        } else {
          // Erro
          setStatus('error');
        }
      } catch (error) {
        console.error('❌ Erro ao verificar email:', error);
        setStatus('error');
      }
    };
    
    verifyToken();
  }, [token, onSuccess]);

  // ============================================
  // ⏱️ TIMER DE COOLDOWN
  // ============================================
  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    setResendSuccess(false);
    
    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ============================================
  // 📧 REENVIO DE E-MAIL
  // ============================================
  const handleResend = async () => {
    if (cooldown > 0 || resending) return;
    
    setResending(true);
    
    try {
      // 🔥 MOCK: Simular reenvio
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setResendSuccess(true);
      startCooldown();
      
      // Disparar evento
      window.dispatchEvent(new CustomEvent('auth:email:resent', {
        detail: { email, timestamp: new Date().toISOString() }
      }));
      
    } catch (error) {
      console.error('❌ Erro ao reenviar:', error);
    } finally {
      setResending(false);
    }
  };

  // ============================================
  // 🎨 ANIMAÇÕES
  // ============================================
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
        when: 'beforeChildren',
        staggerChildren: 0.1
      }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 }
  };

  // ============================================
  // 🎨 RENDER POR ESTADO
  // ============================================

  // Estado de verificação
  if (status === 'verifying') {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="w-full max-w-md mx-auto text-center py-12"
      >
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { repeat: Infinity, duration: 2, ease: 'linear' },
            scale: { repeat: Infinity, duration: 2 }
          }}
          className="w-20 h-20 mx-auto mb-6"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
            <RefreshCw className="w-10 h-10 text-white" />
          </div>
        </motion.div>
        
        <motion.h2 
          variants={itemVariants}
          className="text-2xl font-bold text-slate-900 mb-3"
        >
          Verificando seu e-mail
        </motion.h2>
        
        <motion.p 
          variants={itemVariants}
          className="text-slate-600"
        >
          Aguarde um momento enquanto validamos seu link...
        </motion.p>

        {/* Loading dots */}
        <motion.div 
          variants={itemVariants}
          className="flex justify-center gap-1 mt-4"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{ 
                scale: [1, 1.5, 1],
                opacity: [0.5, 1, 0.5]
              }}
              transition={{
                repeat: Infinity,
                duration: 1,
                delay: i * 0.2
              }}
              className="w-2 h-2 bg-blue-500 rounded-full"
            />
          ))}
        </motion.div>
      </motion.div>
    );
  }

  // Estado de sucesso
  if (status === 'success') {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="w-full max-w-md mx-auto"
      >
        <motion.div 
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center"
        >
          {/* Ícone animado */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: 'spring', 
              stiffness: 200,
              delay: 0.2
            }}
            className="relative mb-6"
          >
            {/* Círculo de fundo */}
            <div className="absolute inset-0 bg-emerald-100 rounded-full blur-xl opacity-50" />
            
            {/* Ícone principal */}
            <div className="relative w-24 h-24 mx-auto">
              <motion.div
                animate={{ 
                  scale: [1, 1.2, 1],
                  rotate: [0, 10, -10, 0]
                }}
                transition={{ 
                  delay: 0.5,
                  duration: 0.5
                }}
                className="w-full h-full bg-gradient-to-br from-emerald-500 to-green-500 rounded-2xl flex items-center justify-center shadow-xl"
              >
                <CheckCircle className="w-12 h-12 text-white" />
              </motion.div>
            </div>
          </motion.div>
          
          <motion.h2 
            variants={itemVariants}
            className="text-2xl font-bold text-slate-900 mb-3"
          >
            E-mail verificado!
          </motion.h2>
          
          <motion.p 
            variants={itemVariants}
            className="text-slate-600 mb-6"
          >
            Seu e-mail <span className="font-medium text-slate-900">{email}</span> foi verificado com sucesso.
          </motion.p>

          {/* Confetes animados (simulados) */}
          <motion.div 
            variants={itemVariants}
            className="flex justify-center gap-2 mb-6"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <motion.div
                key={i}
                initial={{ y: 0, opacity: 0 }}
                animate={{ 
                  y: [-20, -40, -20],
                  opacity: [0, 1, 0],
                  rotate: [0, 180, 360]
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2,
                  delay: i * 0.1
                }}
              >
                <Sparkles className={`w-4 h-4 ${
                  i % 2 === 0 ? 'text-yellow-500' : 'text-emerald-500'
                }`} />
              </motion.div>
            ))}
          </motion.div>
          
          <motion.div 
            variants={itemVariants}
            className="space-y-3"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate('/login')}
              className="w-full py-3.5 px-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-green-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Ir para o login
            </motion.button>
            
            <button
              onClick={onBackToLogin}
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft className="w-3 h-3" />
              Voltar
            </button>
          </motion.div>
        </motion.div>

        {/* Mensagem de boas-vindas */}
        <motion.div
          variants={itemVariants}
          className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-900 mb-1">
                Conta verificada!
              </p>
              <p className="text-xs text-blue-700">
                Agora você pode acessar todos os recursos da plataforma. 
                Seu ambiente multi-tenant está pronto.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Estado de token expirado
  if (status === 'expired') {
    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="w-full max-w-md mx-auto"
      >
        <motion.div 
          variants={itemVariants}
          className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg"
          >
            <Clock className="w-10 h-10 text-white" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Link expirado
          </h2>
          
          <p className="text-slate-600 mb-6">
            O link de verificação expirou. Solicite um novo e-mail de verificação.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={handleResend}
              disabled={cooldown > 0 || resending}
              className={`
                w-full py-3.5 px-4 rounded-xl font-semibold
                transition-all flex items-center justify-center gap-2
                ${cooldown > 0 || resending
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg'
                }
              `}
            >
              {resending ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                    className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                  />
                  Enviando...
                </>
              ) : cooldown > 0 ? (
                <>
                  <Clock className="w-4 h-4" />
                  Reenviar em {cooldown}s
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Reenviar e-mail
                </>
              )}
            </button>
            
            <button
              onClick={onBackToLogin}
              className="w-full py-3 px-4 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o login
            </button>
          </div>

          <AnimatePresence>
            {resendSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-2"
              >
                <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                <p className="text-xs text-emerald-700">
                  Novo e-mail enviado! Verifique sua caixa de entrada.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </motion.div>
    );
  }

  // Estado de erro
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="w-full max-w-md mx-auto"
    >
      <motion.div 
        variants={itemVariants}
        className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
          className="w-20 h-20 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg"
        >
          <AlertCircle className="w-10 h-10 text-white" />
        </motion.div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-3">
          Link inválido
        </h2>
        
        <p className="text-slate-600 mb-6">
          O link que você usou não é válido. Verifique o link ou solicite um novo.
        </p>
        
        <div className="space-y-3">
          <button
            onClick={handleResend}
            className="w-full py-3.5 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
          >
            Solicitar novo link
          </button>
          
          <button
            onClick={onBackToLogin}
            className="w-full py-3 px-4 text-slate-600 hover:text-slate-900 transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </button>
        </div>

        {/* Ajuda adicional */}
        <motion.div
          variants={itemVariants}
          className="mt-6 p-4 bg-slate-50 rounded-xl text-left"
        >
          <p className="text-xs font-medium text-slate-700 mb-2">
            Possíveis causas:
          </p>
          <ul className="space-y-1 text-xs text-slate-600">
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-slate-400 rounded-full" />
              Link pode ter expirado (válido por 24h)
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-slate-400 rounded-full" />
              O e-mail já foi verificado anteriormente
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1 h-1 bg-slate-400 rounded-full" />
              Problema temporário no sistema
            </li>
          </ul>
        </motion.div>
      </motion.div>

      {/* Debug info (apenas desenvolvimento) */}
      {process.env.NODE_ENV === 'development' && token && (
        <motion.div
          variants={itemVariants}
          className="mt-4 p-3 bg-slate-100 rounded-xl text-xs"
        >
          <p className="font-medium text-slate-700 mb-1">🔧 Debug - Token:</p>
          <p className="text-slate-600 break-all">{token}</p>
        </motion.div>
      )}
    </motion.div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(VerifyEmail);